import { useState } from "react";
import { getSidebarStyles } from "./Sidebar.styles";
import type { SidebarNavItem, SidebarProps } from "./Sidebar.types";
import { NavItem } from "../../molecules/NavItem";
import { ProfileButton } from "../../molecules/ProfileButton";
import { Button } from "../../atoms/Button";
import { Icon } from "../../atoms/Icon";
import { useSidebar } from "./useSidebar";
import sidebarBg from "@/assets/sidebar-bg-alt-02.png";
import { ActionMenu } from "@/components/molecules";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { cn } from "@/utils/cn";

type SidebarNavItemWithExpand = SidebarNavItem & { _expanded?: boolean };

const NavGroup = ({
  item,
  isCollapsed,
}: {
  item: SidebarNavItemWithExpand;
  isCollapsed: boolean;
}) => {
  const location = useLocation();
  const isChildActive = item.children?.some((c) =>
    location.pathname.startsWith(String(c.to))
  );
  const [open, setOpen] = useState(!!isChildActive);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "text-white/70 hover:bg-white/10 hover:text-white",
          isChildActive && "bg-white/10 text-white"
        )}
      >
        <Icon name={item.icon} />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            <Icon name={open ? "angle-up" : "angle-down"} />
          </>
        )}
      </button>

      {open && !isCollapsed && item.children && (
        <ul className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-2 text-xs">
          {item.children.map((child) => (
            <li key={child.label}>
              <NavItem
                to={child.to}
                icon={child.icon}
                disabled={child.disabled}
                isCollapsed={false}
              >
                {child.label}
              </NavItem>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export const Sidebar = ({
  logoFull,
  logoSmall,
  navItems,
  profile,
  footer,
  collapsable = false,
}: SidebarProps) => {
  const { isCollapsed, toggleCollapse } = useSidebar();
  const styles = getSidebarStyles({ isCollapsed });
  const { logout } = useAuth();

  const actionMenuItems = [
    {
      icon: "log-out",
      id: "1",
      label: "Sair",
      onClick: () => logout(),
    },
  ];

  return (
    <section
      className={styles.root}
      style={{ backgroundImage: `url(${sidebarBg})` }}
    >
      <div className="flex flex-1 flex-col">
        <header className={styles.header}>
          <div>{isCollapsed ? logoSmall : logoFull}</div>
          {collapsable && (
            <div className={styles.toggleButton}>
              <Button
                iconOnly
                size="xs"
                icon={isCollapsed ? "angle-circle-right" : "angle-circle-left"}
                onClick={toggleCollapse}
              />
            </div>
          )}
        </header>

        <nav className={styles.navigation}>
          <ul className={styles.list}>
            {navItems.map((item) =>
              item.children && item.children.length > 0 ? (
                <NavGroup
                  key={item.label}
                  item={item}
                  isCollapsed={isCollapsed}
                />
              ) : (
                <li key={item.label}>
                  <NavItem
                    to={item.to}
                    icon={item.icon}
                    disabled={item.disabled}
                    isCollapsed={isCollapsed}
                  >
                    {item.label}
                  </NavItem>
                </li>
              )
            )}
          </ul>
        </nav>
      </div>

      <footer className={styles.footer}>
        {!footer ? (
          <ActionMenu
            align="end"
            items={actionMenuItems}
            side="right"
            trigger={
              <ProfileButton
                name={profile.name}
                role={profile.role}
                avatarProps={{
                  alt: profile.name,
                  size: isCollapsed ? "sm" : "md",
                  src: profile.avatarSrc,
                }}
                isCollapsed={isCollapsed}
              />
            }
          />
        ) : (
          footer
        )}
      </footer>
    </section>
  );
};
